/**
 * Environment Variables & AI Configuration Settings
 * Inspired by Macula task-master, adapted for R4S OmniMens with Claude AI focus.
 *
 * Features:
 * - AI Configuration summary card with Claude-specific settings
 * - Environment variable list with category filter & search
 * - Create/Edit modal with auto-detection (category, sensitivity)
 * - Masked sensitive values with reveal toggle
 * - Category stats overview
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { environmentApi } from '../../api/client';
import {
  useEnvironmentVariables,
  useEnvVarStats,
  useAIConfig,
  useCreateEnvVar,
  useUpdateEnvVar,
  useDeleteEnvVar,
  useRevealEnvVar,
  useTestConnection,
  useUpdateAIConfig,
  getCategoryInfo,
  detectCategory,
  detectSensitive,
  ENV_CATEGORIES,
} from '../../hooks/useEnvironmentData';
import type {
  EnvironmentVariableListItem,
  EnvironmentVariableCreateRequest,
  AIConfigUpdateRequest,
  EnvVarCategory,
} from '../../types';
import { Modal } from '../ui/Modal';
import {
  Brain,
  Key,
  Database,
  Settings,
  Search,
  Plus,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  Shield,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Save,
  Zap,
  RefreshCw,
  Info,
  Plug,
  Mail,
  Radio,
  Layers,
  Flag,
  Circle,
  Copy,
  Thermometer,
  Hash,
  Clock,
  Gauge,
  Wifi,
} from 'lucide-react';
import clsx from 'clsx';

// ============ Icon Map ============

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  Brain,
  Key,
  Database,
  Settings,
  Mail,
  Plug,
  Radio,
  Layers,
  Flag,
  Circle,
};

function CategoryIcon({
  category,
  className,
}: {
  category: EnvVarCategory;
  className?: string;
}) {
  const info = getCategoryInfo(category);
  const Icon = CATEGORY_ICONS[info.icon] ?? Circle;
  return <Icon className={clsx('w-4 h-4', className)} />;
}

// ============ AI Config Card ============

function AIConfigCard() {
  const aiConfig = useAIConfig();
  const isLoading = aiConfig.isLoading;
  const data = aiConfig.data;
  const hasError = aiConfig.isError;
  const [showEditModal, setShowEditModal] = useState(false);

  return (
    <>
      <div className="bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 border border-purple-200 rounded-xl p-6">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-purple-100 rounded-xl flex items-center justify-center">
              <Brain className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                AI / Claude Configuration
              </h3>
              <p className="text-xs text-gray-500">
                Anthropic Claude API Settings
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowEditModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-white/70 border border-purple-200 rounded-lg hover:bg-white hover:border-purple-300 transition-colors"
              title="Edit AI Configuration"
            >
              <Edit3 className="w-3.5 h-3.5" />
              Edit
            </button>
            {data?.api_key_configured ? (
              <span className="flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
                <ShieldCheck className="w-3.5 h-3.5" />
                Connected
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
                <AlertTriangle className="w-3.5 h-3.5" />
                Not configured
              </span>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
          </div>
        ) : hasError ? (
          <div className="bg-white/60 rounded-lg p-4 text-sm text-gray-500">
            <p>
              AI configuration could not be loaded.{' '}
              <span className="text-purple-600 font-medium">
                Check the backend connection.
              </span>
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <ConfigItem
              icon={Brain}
              label="Model"
              value={data?.model || 'claude-sonnet-4-20250514'}
              color="text-purple-600"
            />
            <ConfigItem
              icon={Thermometer}
              label="Temperature"
              value={String(data?.temperature ?? 0.1)}
              color="text-orange-600"
            />
            <ConfigItem
              icon={Hash}
              label="Max Tokens"
              value={String(data?.max_tokens ?? 16384)}
              color="text-blue-600"
            />
            <ConfigItem
              icon={Clock}
              label="Timeout"
              value={`${data?.timeout ?? 300}s`}
              color="text-green-600"
            />
            <ConfigItem
              icon={RefreshCw}
              label="Retries"
              value={String(data?.max_retries ?? 3)}
              color="text-indigo-600"
            />
            <ConfigItem
              icon={Gauge}
              label="Rate Limit"
              value={`${data?.rate_limit_rpm ?? 50} RPM`}
              color="text-cyan-600"
            />
          </div>
        )}
      </div>

      {/* AI Config Edit Modal */}
      <AIConfigEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        currentConfig={data}
      />
    </>
  );
}

// ============ AI Config Edit Modal ============

function AIConfigEditModal({
  isOpen,
  onClose,
  currentConfig,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentConfig?: {
    model?: string;
    temperature?: number;
    max_tokens?: number;
    timeout?: number;
    max_retries?: number;
    rate_limit_rpm?: number;
  } | null;
}) {
  const updateMutation = useUpdateAIConfig();

  const [model, setModel] = useState('');
  const [temperature, setTemperature] = useState('');
  const [maxTokens, setMaxTokens] = useState('');
  const [timeout, setTimeout_] = useState('');
  const [maxRetries, setMaxRetries] = useState('');
  const [rateLimitRpm, setRateLimitRpm] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');

  // Sync form when modal opens or config changes
  useEffect(() => {
    if (isOpen && currentConfig) {
      setModel(currentConfig.model || 'claude-sonnet-4-20250514');
      setTemperature(String(currentConfig.temperature ?? 0.1));
      setMaxTokens(String(currentConfig.max_tokens ?? 16384));
      setTimeout_(String(currentConfig.timeout ?? 300));
      setMaxRetries(String(currentConfig.max_retries ?? 3));
      setRateLimitRpm(String(currentConfig.rate_limit_rpm ?? 50));
      setValidationErrors({});
      setSuccessMessage('');
    }
  }, [isOpen, currentConfig]);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!model.trim()) errors.model = 'Model must not be empty';
    const temp = parseFloat(temperature);
    if (isNaN(temp) || temp < 0 || temp > 1) errors.temperature = '0.0 – 1.0';
    const tokens = parseInt(maxTokens);
    if (isNaN(tokens) || tokens < 1 || tokens > 100000) errors.max_tokens = '1 – 100.000';
    const to = parseInt(timeout);
    if (isNaN(to) || to < 10 || to > 600) errors.timeout = '10 – 600 seconds';
    const retries = parseInt(maxRetries);
    if (isNaN(retries) || retries < 0 || retries > 10) errors.max_retries = '0 – 10';
    const rpm = parseInt(rateLimitRpm);
    if (isNaN(rpm) || rpm < 1 || rpm > 10000) errors.rate_limit_rpm = '1 – 10.000';
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      await updateMutation.mutateAsync({
        model: model.trim(),
        temperature: parseFloat(temperature),
        max_tokens: parseInt(maxTokens),
        timeout: parseInt(timeout),
        max_retries: parseInt(maxRetries),
        rate_limit_rpm: parseInt(rateLimitRpm),
      });
      setSuccessMessage('AI configuration saved! Changes take effect immediately — no restart required.');
      // Auto-close after brief success display
      window.setTimeout(() => {
        onClose();
      }, 1500);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit AI Configuration">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Model Section */}
        <div className="space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-2">
            <Brain className="w-3.5 h-3.5" />
            AI Model
          </h4>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Claude Model
            </label>
            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="e.g. claude-sonnet-4-20250514"
              className={clsx(
                'w-full px-3 py-2 border rounded-lg text-sm font-mono',
                'focus:ring-2 focus:ring-purple-200 focus:border-purple-400',
                validationErrors.model ? 'border-red-300 bg-red-50' : 'border-gray-300'
              )}
            />
            {validationErrors.model && (
              <p className="text-xs text-red-500 mt-1">{validationErrors.model}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Temperature
              <span className="text-xs text-gray-400 ml-2">(0.0 = deterministic, 1.0 = creative)</span>
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={temperature}
                onChange={(e) => setTemperature(e.target.value)}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
              <input
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={temperature}
                onChange={(e) => setTemperature(e.target.value)}
                className={clsx(
                  'w-20 px-2 py-1.5 border rounded-lg text-sm text-center',
                  validationErrors.temperature ? 'border-red-300 bg-red-50' : 'border-gray-300'
                )}
              />
            </div>
            {validationErrors.temperature && (
              <p className="text-xs text-red-500 mt-1">{validationErrors.temperature}</p>
            )}
          </div>
        </div>

        {/* Limits Section */}
        <div className="space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-2">
            <Settings className="w-3.5 h-3.5" />
            Limits & Timeouts
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Tokens
              </label>
              <input
                type="number"
                min="1"
                max="100000"
                value={maxTokens}
                onChange={(e) => setMaxTokens(e.target.value)}
                className={clsx(
                  'w-full px-3 py-2 border rounded-lg text-sm',
                  'focus:ring-2 focus:ring-purple-200 focus:border-purple-400',
                  validationErrors.max_tokens ? 'border-red-300 bg-red-50' : 'border-gray-300'
                )}
              />
              {validationErrors.max_tokens && (
                <p className="text-xs text-red-500 mt-1">{validationErrors.max_tokens}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timeout (seconds)
              </label>
              <input
                type="number"
                min="10"
                max="600"
                value={timeout}
                onChange={(e) => setTimeout_(e.target.value)}
                className={clsx(
                  'w-full px-3 py-2 border rounded-lg text-sm',
                  'focus:ring-2 focus:ring-purple-200 focus:border-purple-400',
                  validationErrors.timeout ? 'border-red-300 bg-red-50' : 'border-gray-300'
                )}
              />
              {validationErrors.timeout && (
                <p className="text-xs text-red-500 mt-1">{validationErrors.timeout}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Retries
              </label>
              <input
                type="number"
                min="0"
                max="10"
                value={maxRetries}
                onChange={(e) => setMaxRetries(e.target.value)}
                className={clsx(
                  'w-full px-3 py-2 border rounded-lg text-sm',
                  'focus:ring-2 focus:ring-purple-200 focus:border-purple-400',
                  validationErrors.max_retries ? 'border-red-300 bg-red-50' : 'border-gray-300'
                )}
              />
              {validationErrors.max_retries && (
                <p className="text-xs text-red-500 mt-1">{validationErrors.max_retries}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rate Limit (RPM)
              </label>
              <input
                type="number"
                min="1"
                max="10000"
                value={rateLimitRpm}
                onChange={(e) => setRateLimitRpm(e.target.value)}
                className={clsx(
                  'w-full px-3 py-2 border rounded-lg text-sm',
                  'focus:ring-2 focus:ring-purple-200 focus:border-purple-400',
                  validationErrors.rate_limit_rpm ? 'border-red-300 bg-red-50' : 'border-gray-300'
                )}
              />
              {validationErrors.rate_limit_rpm && (
                <p className="text-xs text-red-500 mt-1">{validationErrors.rate_limit_rpm}</p>
              )}
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-start gap-2">
          <Info className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-purple-700">
            Changes take effect immediately — no Docker restart required. Values are stored in the database and reloaded on every AI calculation.
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        )}

        {/* Error */}
        {updateMutation.isError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
            <p className="text-sm text-red-700">
              Error saving configuration. Please check your inputs and backend connection.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className={clsx(
              'flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-colors',
              updateMutation.isPending
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            )}
          >
            {updateMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ConfigItem({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="bg-white/70 rounded-lg px-3 py-2.5 border border-white/50">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={clsx('w-3.5 h-3.5', color)} />
        <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-400">
          {label}
        </span>
      </div>
      <p className={clsx('text-sm font-semibold', color)}>{value}</p>
    </div>
  );
}

// ============ Stats Cards ============

function StatsCards() {
  const stats = useEnvVarStats();
  const data = stats.data;

  if (stats.isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="h-20 bg-gray-50 border border-gray-200 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  const cards = [
    {
      label: 'Total',
      value: data?.total ?? 0,
      color: 'text-gray-700',
      bg: 'bg-gray-50 border-gray-200',
      icon: Settings,
    },
    {
      label: 'AI / LLM',
      value: data?.by_category?.ai ?? 0,
      color: 'text-purple-700',
      bg: 'bg-purple-50 border-purple-200',
      icon: Brain,
    },
    {
      label: 'Sensitive',
      value: data?.sensitive_count ?? 0,
      color: 'text-amber-700',
      bg: 'bg-amber-50 border-amber-200',
      icon: Shield,
    },
    {
      label: 'Active',
      value: data?.active_count ?? 0,
      color: 'text-green-700',
      bg: 'bg-green-50 border-green-200',
      icon: CheckCircle2,
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {cards.map((card) => (
        <div
          key={card.label}
          className={clsx(
            'border rounded-lg p-4 flex items-center gap-3',
            card.bg
          )}
        >
          <card.icon className={clsx('w-7 h-7', card.color)} />
          <div>
            <p className={clsx('text-xl font-bold', card.color)}>
              {card.value}
            </p>
            <p className="text-[11px] text-gray-500">{card.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ Variable Row ============

function VariableRow({
  variable,
  onEdit,
  onDelete,
}: {
  variable: EnvironmentVariableListItem;
  onEdit: (v: EnvironmentVariableListItem) => void;
  onDelete: (v: EnvironmentVariableListItem) => void;
}) {
  const [revealed, setRevealed] = useState(false);
  const [revealedValue, setRevealedValue] = useState<string | null>(null);
  const revealMutation = useRevealEnvVar();

  const catInfo = getCategoryInfo(variable.category);

  const handleReveal = async () => {
    if (revealed) {
      setRevealed(false);
      setRevealedValue(null);
      return;
    }
    try {
      const result = await revealMutation.mutateAsync(variable.id);
      setRevealedValue(result.value);
      setRevealed(true);
      // Auto-hide after 10s
      setTimeout(() => {
        setRevealed(false);
        setRevealedValue(null);
      }, 10000);
    } catch {
      // Error handled by mutation
    }
  };

  const handleCopy = () => {
    const val = revealedValue || variable.masked_value;
    navigator.clipboard.writeText(val);
  };

  return (
    <div className="group flex items-center gap-4 px-4 py-3 border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
      {/* Category Badge */}
      <div
        className={clsx(
          'flex items-center justify-center w-8 h-8 rounded-lg border',
          catInfo.bgColor
        )}
      >
        <CategoryIcon category={variable.category} className={catInfo.color} />
      </div>

      {/* Key & Description */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <code className="text-sm font-semibold text-gray-900 font-mono">
            {variable.key}
          </code>
          {variable.is_sensitive && (
            <Shield className="w-3.5 h-3.5 text-amber-500" />
          )}
          {!variable.is_active && (
            <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-gray-100 text-gray-400 font-medium">
              Inactive
            </span>
          )}
          {variable.is_required && (
            <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-red-50 text-red-500 font-medium">
              Required
            </span>
          )}
        </div>
        <p className="text-xs text-gray-400 truncate mt-0.5">
          {variable.description || 'No description'}
        </p>
      </div>

      {/* Value */}
      <div className="w-52 flex items-center gap-1.5">
        <code
          className={clsx(
            'text-xs font-mono truncate flex-1 px-2 py-1 rounded',
            variable.is_sensitive
              ? 'bg-amber-50 text-amber-700'
              : 'bg-gray-50 text-gray-600'
          )}
        >
          {revealed && revealedValue
            ? revealedValue
            : variable.masked_value || variable.default_value || '—'}
        </code>
        {variable.is_sensitive && (
          <button
            onClick={handleReveal}
            disabled={revealMutation.isPending}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title={revealed ? 'Hide' : 'Show'}
          >
            {revealMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : revealed ? (
              <EyeOff className="w-3.5 h-3.5" />
            ) : (
              <Eye className="w-3.5 h-3.5" />
            )}
          </button>
        )}
        <button
          onClick={handleCopy}
          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          title="Copy"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Category Tag */}
      <span
        className={clsx(
          'text-[10px] uppercase font-medium px-2 py-1 rounded-full border whitespace-nowrap',
          catInfo.bgColor,
          catInfo.color
        )}
      >
        {catInfo.label}
      </span>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onEdit(variable)}
          className="p-1.5 text-gray-400 hover:text-[#45bdd6] hover:bg-[#45bdd6]/10 rounded transition-colors"
          title="Edit"
        >
          <Edit3 className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onDelete(variable)}
          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ============ Create/Edit Modal ============

function EnvVarModal({
  isOpen,
  onClose,
  editVariable,
}: {
  isOpen: boolean;
  onClose: () => void;
  editVariable?: EnvironmentVariableListItem | null;
}) {
  const createMutation = useCreateEnvVar();
  const updateMutation = useUpdateEnvVar();
  const isEditing = !!editVariable;

  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [category, setCategory] = useState<EnvVarCategory>('system');
  const [description, setDescription] = useState('');
  const [isSensitive, setIsSensitive] = useState(false);
  const [isRequired, setIsRequired] = useState(false);

  // Sync form fields when editVariable changes (open modal for editing)
  useEffect(() => {
    if (editVariable) {
      setKey(editVariable.key);
      // For non-sensitive variables, pre-fill the current value so
      // the user can see and adjust it.  Sensitive values are masked
      // in the API response, so we leave the field empty (the user
      // must re-enter the value to change it).
      setValue(editVariable.is_sensitive ? '' : (editVariable.masked_value || editVariable.default_value || ''));
      setCategory(editVariable.category);
      setDescription(editVariable.description ?? '');
      setIsSensitive(editVariable.is_sensitive);
      setIsRequired(editVariable.is_required);
    } else {
      // Reset for create mode
      setKey('');
      setValue('');
      setCategory('system');
      setDescription('');
      setIsSensitive(false);
      setIsRequired(false);
    }
  }, [editVariable]);

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Auto-detect on key change
  const handleKeyChange = (newKey: string) => {
    const formatted = newKey.toUpperCase().replace(/[^A-Z0-9_]/g, '');
    setKey(formatted);
    if (!isEditing) {
      setCategory(detectCategory(formatted));
      setIsSensitive(detectSensitive(formatted));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditing && editVariable) {
        // For sensitive variables, only send value if the user entered
        // something (empty means "keep current encrypted value").
        // For non-sensitive variables, always send the value.
        const shouldSendValue = isSensitive ? !!value : true;
        await updateMutation.mutateAsync({
          id: editVariable.id,
          data: {
            ...(shouldSendValue ? { value } : {}),
            category,
            description,
            is_sensitive: isSensitive,
            is_required: isRequired,
          },
        });
      } else {
        await createMutation.mutateAsync({
          key,
          value,
          category,
          description,
          is_sensitive: isSensitive,
          is_required: isRequired,
        });
      }
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Variable' : 'New Variable'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Key */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Key
          </label>
          <input
            type="text"
            value={key}
            onChange={(e) => handleKeyChange(e.target.value)}
            disabled={isEditing}
            placeholder="Z.B. ANTHROPIC_API_KEY"
            className={clsx(
              'w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm',
              'focus:ring-2 focus:ring-[#45bdd6]/20 focus:border-[#45bdd6]',
              isEditing && 'bg-gray-50 text-gray-500'
            )}
            required
          />
          {!isEditing && key && (
            <div className="flex items-center gap-2 mt-1.5">
              <span
                className={clsx(
                  'text-[10px] uppercase font-medium px-2 py-0.5 rounded-full border',
                  getCategoryInfo(category).bgColor,
                  getCategoryInfo(category).color
                )}
              >
                {getCategoryInfo(category).label}
              </span>
              {isSensitive && (
                <span className="text-[10px] text-amber-600 flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  Sensitive detected
                </span>
              )}
            </div>
          )}
        </div>

        {/* Value */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Value {isEditing && '(empty = unchanged)'}
          </label>
          <input
            type={isSensitive ? 'password' : 'text'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={
              isEditing
                ? 'Enter new value...'
                : 'Enter value...'
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#45bdd6]/20 focus:border-[#45bdd6]"
            required={!isEditing}
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            value={category}
            onChange={(e) =>
              setCategory(e.target.value as EnvVarCategory)
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#45bdd6]/20 focus:border-[#45bdd6]"
          >
            {Object.entries(ENV_CATEGORIES).map(([key, info]) => (
              <option key={key} value={key}>
                {info.label}
              </option>
            ))}
          </select>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#45bdd6]/20 focus:border-[#45bdd6]"
          />
        </div>

        {/* Toggles */}
        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={isSensitive}
              onChange={(e) => setIsSensitive(e.target.checked)}
              className="w-4 h-4 text-[#45bdd6] border-gray-300 rounded focus:ring-[#45bdd6]"
            />
            Sensitive (encrypted)
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
              className="w-4 h-4 text-[#45bdd6] border-gray-300 rounded focus:ring-[#45bdd6]"
            />
            Required field
          </label>
        </div>

        {/* Error */}
        {(createMutation.isError || updateMutation.isError) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
            <p className="text-sm text-red-700">
              Error saving. Check inputs and backend.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              isPending
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-[#45bdd6] text-white hover:bg-[#3ba8c0]'
            )}
          >
            {isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ============ Main Component ============

export function EnvironmentSettings() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editVariable, setEditVariable] =
    useState<EnvironmentVariableListItem | null>(null);

  // Data
  const envVars = useEnvironmentVariables({
    ...(selectedCategory ? { category: selectedCategory } : {}),
    ...(searchQuery ? { search: searchQuery } : {}),
  });

  const variables = envVars.data ?? [];

  // Filtered
  const filteredVariables = useMemo(() => {
    if (!searchQuery && !selectedCategory) return variables;
    return variables.filter((v) => {
      if (selectedCategory && v.category !== selectedCategory) return false;
      if (
        searchQuery &&
        !v.key.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !v.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
        return false;
      return true;
    });
  }, [variables, searchQuery, selectedCategory]);

  const handleEdit = (v: EnvironmentVariableListItem) => {
    setEditVariable(v);
    setShowModal(true);
  };

  const deleteMutation = useDeleteEnvVar();

  const handleDelete = (v: EnvironmentVariableListItem) => {
    if (confirm(`Really delete variable "${v.key}"?`)) {
      deleteMutation.mutate(v.id);
    }
  };

  const handleCreate = () => {
    setEditVariable(null);
    setShowModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Environment Variables & AI Setup
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Configure system, AI and integration settings
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => envVars.refetch()}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3ba8c0] text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Variable
          </button>
        </div>
      </div>

      {/* AI Config Card */}
      <AIConfigCard />

      {/* Stats */}
      <StatsCards />

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search variables..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#45bdd6]/20 focus:border-[#45bdd6]"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={() => setSelectedCategory('')}
            className={clsx(
              'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
              !selectedCategory
                ? 'bg-[#45bdd6] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            All
          </button>
          {Object.entries(ENV_CATEGORIES)
            .slice(0, 6)
            .map(([key, info]) => (
              <button
                key={key}
                onClick={() =>
                  setSelectedCategory(
                    selectedCategory === key ? '' : key
                  )
                }
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                  selectedCategory === key
                    ? 'bg-[#45bdd6] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {info.label}
              </button>
            ))}
        </div>
      </div>

      {/* Variable List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center justify-between text-xs font-semibold text-gray-500 uppercase">
            <span className="w-8" />
            <span className="flex-1 ml-4">Variable</span>
            <span className="w-52">Value</span>
            <span className="w-24 text-center">Category</span>
            <span className="w-20" />
          </div>
        </div>

        {envVars.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : filteredVariables.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Key className="w-10 h-10 mb-3" />
            <p className="text-sm font-medium">
              {envVars.isError
                ? 'Backend connection error'
                : 'No variables found'}
            </p>
            <p className="text-xs mt-1 text-gray-400">
              {envVars.isError
                ? 'Check if the backend server is running and reachable'
                : 'Create a new variable with the button above'}
            </p>
          </div>
        ) : (
          filteredVariables.map((v) => (
            <VariableRow
              key={v.id}
              variable={v}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-blue-800">
            Configuration Note
          </p>
          <p className="text-xs text-blue-600 mt-1">
            Changes to sensitive variables (e.g. API Keys) are stored encrypted. Values are initially loaded from the .env file and can be dynamically overridden here. All changes are logged in the audit log.
          </p>
        </div>
      </div>

      {/* Modal */}
      <EnvVarModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditVariable(null);
        }}
        editVariable={editVariable}
      />
    </div>
  );
}
